import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useAnalytics } from "../contexts/analytics/hooks";
import { useSnackbar } from "../contexts/SnackbarContext";
import { backendService } from "../services/backend.service";
import type { Nurse } from "../utils/models";

const NURSES_QUERY_KEY_ROOT = "nurses";
// HospitalDataProvider refetches this same entry after every roster write, so a picker mounting
// shortly after doesn't need its own request to be current.
const ROSTER_STALE_TIME_MS = 5 * 60 * 1000;

const nursesQueryOptions = (departmentId: string | undefined, activeOnDate: string | undefined) => ({
	queryKey: [NURSES_QUERY_KEY_ROOT, departmentId, activeOnDate],
	queryFn: async () => {
		const response = await backendService.getNurses(departmentId, activeOnDate);
		return response.data;
	},
});

export const useNurses = (departmentId?: string, activeOnDate?: string) => {
	return useQuery<Nurse[]>({
		...nursesQueryOptions(departmentId, activeOnDate),
		enabled: false,
	});
};

/**
 * The department's roster for pickers (the profile's nurse switcher, the new-credential popover),
 * fetched on its own instead of read from `hospitalData.nurses`.
 *
 * Shares `useNurses`' cache entry, so it is the same data HospitalDataProvider holds — one request, and
 * the provider's refetches after roster writes reach these pickers too.
 */
export const useDepartmentRoster = (departmentId: string) => {
	const { t } = useTranslation();
	const { showSnackbar } = useSnackbar();
	const { trackError } = useAnalytics();
	const query = useQuery<Nurse[]>({
		...nursesQueryOptions(departmentId, undefined),
		staleTime: ROSTER_STALE_TIME_MS,
	});

	useEffect(() => {
		if (query.isError) {
			trackError("department_roster_fetch_error", { department_id: departmentId });
			showSnackbar(t("error.nurses.data"), "error");
		}
	}, [query.isError, departmentId, trackError, showSnackbar, t]);

	// `roster` is undefined until it loads, and stays so after a failure (already reported above).
	return { roster: query.data, isLoading: query.isLoading, isError: query.isError };
};
